import React, { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Calendar, Loader2, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { apiPost, formatLKR } from "@/lib/api";
import BulkPurchaseFlow from "./BulkPurchaseFlow";
import SuggestionSummaryPanel from "./SuggestionSummaryPanel";
import { toast } from "sonner";

/**
 * MonthlyInvestmentDialog — Phase 7 Stage 9 Part B.
 *
 * Two-step flow:
 *   1. amount + date input
 *   2. analysis + checkbox-based suggestion focused on UNDERWEIGHT sectors
 *
 * Test 4 Fix 3 — When the user clicks a suggested stock at step 2 they
 * navigate to the Stock Detail page. When they hit "Back" the dialog
 * reopens via ?openMonthly=1 and we MUST restore them to step 2 with the
 * exact suggestion + amount they were viewing — NOT step 1 (amount entry).
 *
 * To achieve this we mirror step / amount / date / suggestion to
 * sessionStorage keyed by portfolioId. The state is cleared when the
 * user explicitly closes or completes the flow.
 */
const _miStorageKey = (portfolioId) => `cse:mi-state:${portfolioId || "default"}`;

const _readSavedState = (portfolioId) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(_miStorageKey(portfolioId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.step === 2 && parsed.suggestion) return parsed;
  } catch (_e) { /* ignore */ }
  return null;
};

const _clearSavedState = (portfolioId) => {
  if (typeof window === "undefined") return;
  try { sessionStorage.removeItem(_miStorageKey(portfolioId)); } catch (_e) { /* ignore */ }
};

const _writeSavedState = (portfolioId, state) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(_miStorageKey(portfolioId), JSON.stringify(state));
  } catch (_e) { /* ignore */ }
};

const MonthlyInvestmentDialog = ({ open, onClose, portfolioId, portfolioName, onAdded }) => {
  const today = new Date().toISOString().slice(0, 10);
  // Test 4 Fix 3 — restore from sessionStorage if user is returning from
  // the stock detail page mid-flow.
  const restored = useRef(false);
  const saved = open ? _readSavedState(portfolioId) : null;
  const [step, setStep] = useState(saved ? 2 : 1);
  const [amount, setAmount] = useState(saved?.amount != null ? String(saved.amount) : "100000");
  const [date, setDate] = useState(saved?.date || today);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(saved?.suggestion || null);

  // When the dialog re-opens (e.g. via ?openMonthly=1) and there is a
  // saved step-2 state in sessionStorage, restore it exactly once per
  // open transition. We reset the gate when the dialog closes so a
  // subsequent reopen also restores correctly.
  useEffect(() => {
    if (!open) {
      restored.current = false;
      return;
    }
    if (restored.current) return;
    const s = _readSavedState(portfolioId);
    if (s && s.step === 2 && s.suggestion) {
      setStep(2);
      setSuggestion(s.suggestion);
      if (s.amount != null) setAmount(String(s.amount));
      if (s.date) setDate(s.date);
    }
    restored.current = true;
  }, [open, portfolioId]);

  // Mirror current step-2 state to sessionStorage so it survives a stock
  // detail round-trip. We only persist while in step 2 — step 1 is the
  // amount entry screen and is NEVER what we want users to land on.
  useEffect(() => {
    if (!open) return;
    if (step === 2 && suggestion) {
      _writeSavedState(portfolioId, {
        step: 2,
        amount: parseFloat(amount) || null,
        date,
        suggestion,
        savedAt: Date.now(),
      });
    }
  }, [open, step, suggestion, amount, date, portfolioId]);

  const reset = () => {
    setStep(1);
    setAmount("100000");
    setDate(today);
    setSuggestion(null);
    _clearSavedState(portfolioId);
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const analyse = async () => {
    const a = parseFloat(amount);
    if (!a || a <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      const res = await apiPost(`/portfolios/${portfolioId}/monthly-suggest`, {
        amount: a,
      });
      setSuggestion(res);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate suggestion");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="monthly-investment-dialog"
      >
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" />
                Monthly Investment
              </DialogTitle>
              <DialogDescription>
                We'll analyse your current allocation vs targets and suggest stocks
                from <strong>underweight</strong> sectors only — so your monthly
                buys gradually rebalance the portfolio without selling anything.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="mi-amount" className="text-xs">
                  New funds to invest (LKR)
                </Label>
                <Input
                  id="mi-amount"
                  type="number"
                  min="1"
                  step="1000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="mi-amount-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mi-date" className="text-xs">
                  Investment date
                </Label>
                <Input
                  id="mi-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="mi-date-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={analyse} disabled={busy} data-testid="mi-analyse-btn">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}
                Analyse & suggest →
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Suggested for this month</DialogTitle>
              <DialogDescription>{suggestion?.message}</DialogDescription>
            </DialogHeader>

            {/* Phase 4 (Item 16) — Explainer card so users understand
                how the monthly allocation is computed and why these
                specific stocks are suggested. */}
            <div
              className="rounded-md border border-dashed border-primary/30 bg-primary/5 p-3 text-xs leading-relaxed space-y-1"
              data-testid="mi-allocation-explainer"
            >
              <div className="font-medium text-foreground">How is this allocation calculated?</div>
              <p className="text-muted-foreground">
                We compare your current sector mix to the target mix
                Claude generated when you set up the portfolio. Sectors
                more than 1–2% below target are flagged{" "}
                <span className="font-medium text-foreground">underweight</span>{" "}
                and we suggest stocks from those sectors only — gradually
                rebalancing your portfolio without selling.
              </p>
              <p className="text-muted-foreground">
                Your monthly LKR is split across the suggested picks
                proportional to their composite score (higher score =
                bigger share). All dividend estimates are{" "}
                <span className="font-medium text-foreground">net of 10% WHT</span>.
                Tick or untick stocks below to customise — amounts adjust
                instantly.
              </p>
            </div>

            {/* Phase 9 — Monthly Investment summary panel + Phase 7 macro/geo */}
            {suggestion?.summary && (
              <SuggestionSummaryPanel
                summary={suggestion.summary}
                investmentAmount={suggestion.amount}
                title="Monthly Investment Analysis"
                testId="mi-suggestion-summary"
                macroGeo={suggestion.macro_geo_summary}
                warnings={suggestion.macro_geo_warnings || []}
              />
            )}

            {/* Stage 1 — Brokerage breakdown for the new investment */}
            {suggestion?.estimated_buy_brokerage != null && (
              <div
                data-testid="mi-brokerage-summary"
                className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1"
              >
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Investment</span>
                  <span className="tabular font-medium">{formatLKR(suggestion.amount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Brokerage ({((suggestion.buy_rate || 0.0121) * 100).toFixed(2)}%)
                  </span>
                  <span className="tabular font-medium text-[hsl(var(--destructive))]">
                    −{formatLKR(suggestion.estimated_buy_brokerage)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-border pt-1">
                  <span className="font-medium">Effective investment</span>
                  <span className="tabular font-semibold">
                    {formatLKR(suggestion.effective_invested_after_brokerage)}
                  </span>
                </div>
                <div className="text-[10px] text-muted-foreground pt-0.5">
                  Estimated annual brokerage if invested every month:{" "}
                  <span className="font-medium text-foreground tabular">
                    {formatLKR((suggestion.estimated_buy_brokerage || 0) * 12, { decimals: 0 })}
                  </span>
                </div>
              </div>
            )}

            {/* Sector status summary */}
            {suggestion && (
              <div className="space-y-1.5 text-xs">
                {(suggestion.overweight || []).slice(0, 5).map((r) => (
                  <SectorRow key={r.sector} row={r} status="over" />
                ))}
                {(suggestion.on_target || []).slice(0, 3).map((r) => (
                  <SectorRow key={r.sector} row={r} status="ok" />
                ))}
                {(suggestion.underweight || []).slice(0, 5).map((r) => (
                  <SectorRow key={r.sector} row={r} status="under" />
                ))}
              </div>
            )}

            {/* Stock picks via the same checkbox flow as Part A */}
            {(suggestion?.suggestions?.length || 0) > 0 ? (
              <BulkPurchaseFlow
                portfolioId={portfolioId}
                portfolioName={portfolioName}
                totalAmount={parseFloat(amount)}
                allocations={suggestion.suggestions.map((s) => ({
                  cse_symbol: s.cse_symbol,
                  company_code: s.company_code,
                  company_name: s.company_name,
                  sector: s.sector,
                  score: s.score,
                  shares: s.shares,
                  close_price: s.close_price,
                  allocation_lkr: s.amount_lkr,
                  allocation_pct: s.allocation_pct,
                  expected_annual_dividend_lkr: s.estimated_annual_dividend_lkr,
                  // Phase 9 — pass through signal/score/confidence/sector_verdict
                  composite_signal: s.composite_signal,
                  composite_score: s.composite_score ?? s.score,
                  data_confidence: s.data_confidence,
                  sector_verdict: s.sector_verdict,
                  share_type: s.share_type,
                  // Test 4 Fix 4 — pass through target price metrics
                  target_pe_based: s.target_pe_based,
                  target_average: s.target_average,
                  target_upside_pct: s.target_upside_pct,
                  // Test 6 Fix 1 — canonical weighted base case
                  target_base_case: s.target_base_case,
                  target_base_case_upside_pct: s.target_base_case_upside_pct,
                  reason: `Underweight ${s.sector} (${(s.underweight_pct || 0).toFixed(1)}% below target)`,
                }))}
                ctaLabel="Record monthly purchases"
                onCompleted={() => {
                  onAdded?.();
                  close();
                }}
                testIdPrefix="mi-bulk"
                fromLabel="Monthly Investment"
              />
            ) : (
              <div className="rounded-md border border-warning/30 bg-warning/5 p-3 text-sm text-muted-foreground">
                No stocks suggested. Your portfolio looks well-balanced —
                consider topping up existing positions instead.
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { _clearSavedState(portfolioId); setStep(1); setSuggestion(null); }}
                disabled={busy}
              >
                ← Back
              </Button>
              <Button variant="ghost" onClick={close} disabled={busy}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SectorRow = ({ row, status }) => {
  const cfg = {
    over: {
      icon: AlertTriangle,
      cls: "text-[hsl(var(--warning))]",
      label: row.drift_pct > 15 ? "AVOID — significantly overweight" : "AVOID — slightly over",
    },
    ok: {
      icon: CheckCircle2,
      cls: "text-[hsl(var(--success))]",
      label: "On target",
    },
    under: {
      icon: TrendingUp,
      cls: "text-[hsl(var(--primary))]",
      label: row.drift_pct < -10 ? "PRIORITISE — most underweight" : "CONSIDER — underweight",
    },
  }[status];
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-2 rounded-md border bg-background/40 px-2 py-1.5">
      <Icon className={`h-3.5 w-3.5 ${cfg.cls}`} />
      <span className="font-medium">{row.sector}</span>
      <span className="text-muted-foreground">
        Current {row.current_pct?.toFixed(1)}% vs Target {row.target_pct?.toFixed(1)}%
      </span>
      <Badge variant="outline" className={`ml-auto text-[10px] ${cfg.cls} border-current/30`}>
        {cfg.label}
      </Badge>
    </div>
  );
};

export default MonthlyInvestmentDialog;
